import fetch from "node-fetch";
import { VehicleType } from "../model/vehicleType";

export interface OptibusConfig {
    apiKey: string;
    accountName: string;
    baseUrl: string;
}

interface Holiday {
    groupName: string;
    name: string;
    timeDefinition: {
        from: string;
        to: string;
    };
}

interface Region {
    name: string;
    isArchived: boolean;
    units: {
        name: string;
        isArchived: boolean;
        code: string;
        uuid: string;
    }[];
    uuid: string;
}

interface Driver {
    id: string;
    archived: boolean;
    note: string;
    firstName: string;
    lastName: string;
    uuid: string;
    middleName?: string;
    avatar?: string;
    employmentPeriods: any[]; // Define specific type if needed
}

interface Operation {
    id: string;
    name: string;
    status: 'active' | 'archived';
    regionId: string;
    startDate: string;
    endDate: string;
    createdAt: string;
    updatedAt: string;
}

interface OperationSchedule {
    id: string;
    operationId: string;
    driverId: string;
    date: string;
    startTime: string;
    endTime: string;
    status: 'scheduled' | 'in_progress' | 'completed' | 'cancelled';
}

interface OperationVehicle {
    id: string;
    operationId: string;
    vehicleId: string;
    status: 'assigned' | 'active' | 'maintenance' | 'inactive';
    startDate: string;
    endDate: string;
}

export interface Vehicle {
    id: string;
    licensePlate: string;
    depot: string;
    opUnitId: string;
    description?: string;
    type?: string;
    nextDowntime?: number;
    model?: string;
    archived: boolean;
    archivedOn?: string;
    [key: string]: any; // For additional attributes
}

interface VehicleDepotPeriod {
    vehicleId: string;
    opUnitId: string;
    endTime?: number;
    startTime?: number;
    endDate?: string;
    startDate: string;
    depotId: string;
    type: 'MAIN' | 'LOAN' | 'FIRST';
    periodId: string;
}

interface AttributeTypeDefinition {
    id: string;
    type: 'STRING' | 'BOOLEAN' | 'SINGLE_SELECT' | 'MULTI_SELECT' | 'DATE' | 'NUMBER' | 'DURATION';
    displayName?: string;
}

interface VehicleAssignment {
    originalVehicleId?: string;
    vehicleId?: string;
    blockId: string;
}

interface CalendarVehicleAssignment {
    tasks: VehicleAssignment[];
    date: string;
}

interface TaskAssignment {
    opUnitUuid?: string;
    cancellationReason?: string;
    disabled?: boolean;
    driverId?: string;
    taskId: string;
    attributes?: Record<string, any>;
    labels?: any[];
    vehicleId?: string;
    originalDriverId?: string;
}

interface CalendarDailyInformation {
    tasks: TaskAssignment[];
    date: string;
}

interface CalendarBlock {
    disabled: boolean;
    srcBlockId?: string;
    data?: any;
    summary: {
        type: string;
        [key: string]: any;
    };
    events: any[];
    type: 'block' | 'custom';
    description?: string;
    displayId: string;
    id: string;
}

interface CalendarPlanInformation {
    deployDateRange: {
        from: string;
        to: string;
    };
    group: string;
    rosterId: string;
    plan: string;
}

interface CalendarInformation {
    calendarVehicleAssignments: CalendarVehicleAssignment[];
    blocks: CalendarBlock[];
    tasks: any[];
    DailyTasks: CalendarDailyInformation[];
    deployedPlans: CalendarPlanInformation[];
    depotId: string;
}

interface OperationalPlanEvent {
    uuid: string;
    eventType: string;
    date: string;
    startTime: string;
    endTime: string;
    driverUuid?: string;
    vehicleUuid?: string;
    dutyType?: string;
    noteText?: string;
    reinforcedTripReference?: string;
    splitTripReference?: string;
    originStopName?: string;
    originStopId?: string;
    originStopLabel?: string;
    originDepotName?: string;
    originDepotId?: string;
    originDepotLabel?: string;
    destinationStopName?: string;
    destinationStopId?: string;
    destinationStopLabel?: string;
    destinationDepotName?: string;
    destinationDepotId?: string;
    destinationDepotLabel?: string;
}

interface OperationalPlanDepot {
    depotUuid: string;
    depotName: string;
    events: OperationalPlanEvent[];
}

export interface PrivateHireLocation {
    lng?: number;
    lat?: number;
    address?: string;
    name: string;
}

export interface PrivateHireEvent {
    distance?: number;
    note?: string;
    labelCodes?: string[];
    endLocation?: PrivateHireLocation;
    startLocation?: PrivateHireLocation;
    endTime: string;
    startTime: string;
    type: 'deadhead' | 'custom' | 'break' | 'depot_pull_in' | 'depot_pull_out' | 'private_hire' | 'sign_on' | 'sign_off';
}

export interface PrivateHireTask {
    noVehicleNeeded?: boolean;
    note?: string;
    labelCodes?: string[];
    events: PrivateHireEvent[];
    date: string;
    depotName: string;
    taskId: string;
}

export interface PrivateHireOrder {
    orderStatus: 'confirmed' | 'cancelled';
    orderId: string;
    privateHires: PrivateHireTask[];
}

export interface PrivateHireOrderStatus {
    orderStatus: 'confirmed' | 'cancelled';
}

interface RealTimeOperation {
    operational_actions: {
        action_type: 'cut_duty' | 'assign_duty' | 'unassign_duty' | 'swap_duty_assignment' | 'assign_block' | 'unassign_block';
        operational_date: string;
        entities_changes: {
            blocks: any[];
            block_assignments: any[];
            runs: any[];
            run_assignments: any[];
            run_events: any[];
        }[];
    }[];
    customer: string;
    aggregate_id: string;
    region_id: string;
    created_at: string;
    id: string;
}

interface AttributeValue {
    attributeId: string;
    date: string;
    value: string;
}

interface GetVehicleAttributesResponse {
    attributeMap: {
        [key: string]: AttributeTypeDefinition;
    };
    attributes: {
        [vehicleId: string]: {
            [attributeId: string]: AttributeValue[];
        };
    };
}

export class OptibusClient {
    private headers: HeadersInit;
    private baseUrl: string;

    constructor(config: OptibusConfig) {
        this.baseUrl = config.baseUrl;
        this.headers = {
            'User-Agent': 'curl/8.7.1',
            'Authorization': config.apiKey,
            'X-Optibus-Api-Client': config.accountName,
            'Content-Type': 'application/json'
        };
        console.log('OptibusClient constructor', this.baseUrl, this.headers);
    }

    private async request<T>(path: string, options: RequestInit = {}, retryCount = 0, expectedErrors: string[] = []): Promise<T | null> {
        const url = this.baseUrl.endsWith('/') 
            ? this.baseUrl + path.replace(/^\//, '')
            : this.baseUrl + '/' + path.replace(/^\//, '');
        const fetchOptions: RequestInit = {
            ...options,
            headers: {
                ...this.headers,
                ...(options.headers || {})
            }
        };
        console.log('OptibusClient request', url, fetchOptions);
        const response = await fetch(url, fetchOptions);
        console.log('OptibusClient response', response, response.status, response.statusText, response.headers, response.ok);

        try {
            if (!response.ok) {
                console.log('Status:', response.status);
                console.log('Headers:', Object.fromEntries(response.headers));
                if (response.status >= 500) {
                    throw new Error(`HTTP error!`);
                }

                const responseJson = await response.json?.();
                console.log('OptibusClient JSON response', JSON.stringify(responseJson, null, 2));
                const data = responseJson as T;
                if (data && typeof data === 'object' && 'errors' in data && Array.isArray(data.errors)){
                    if(data.errors.some((e: any) => e.type === 'conflictProcessingRequest') && retryCount < 5) {
                        console.log('Retrying request due to conflict processing error. Waiting', 1000 * (retryCount + 1), 'ms');
                        await new Promise(resolve => setTimeout(resolve, 1000 * (retryCount + 1)));
                        return this.request(path, options, retryCount + 1);
                    } else {
                        const errors = data.errors.filter((e: any) => !expectedErrors.includes(e.type));
                        if(errors.length > 0) {
                            throw new Error(errors.map((e: any) => e.message).join(', '));
                        }
                    }
                }
                return data;
            }
        } catch (error) {
            console.error('Failed to parse JSON response:', error);
            console.error('Response:', response.status, response.statusText, response.headers, error);
            throw new Error(`Response: ${error}`);
        }
    }

    // Preferences
    async getHolidays(startDate?: string, endDate?: string): Promise<Holiday[] | null> {
        const params = new URLSearchParams();
        if (startDate && endDate) {
            params.append('startDate', startDate);
            params.append('endDate', endDate);
        }

        const path = `/preferences/holidays${params.toString() ? '?' + params.toString() : ''}`;
        return this.request<Holiday[]>(path);
    }

    // Regions
    async getRegions(): Promise<Region[] | null> {
        return this.request<Region[]>('/regions');
    }

    // Drivers
    async getDrivers(driversIds?: string[], date?: string): Promise<Driver[] | null> {
        const params = new URLSearchParams();
        if (driversIds) {
            driversIds.forEach(id => params.append('driversIds', id));
        }
        if (date) {
            params.append('date', date);
        }

        const path = `/driver${params.toString() ? '?' + params.toString() : ''}`;
        return this.request<Driver[]>(path);
    }

    // Vehicles
    async getVehicles(vehicleIds?: string[], date?: string): Promise<Vehicle[] | null> {
        const params = new URLSearchParams();
        if (vehicleIds) {
            vehicleIds.forEach(id => params.append('vehicleIds', id));
        }
        if (date) {
            params.append('date', date);
        }

        const path = `/vehicle${params.toString() ? '?' + params.toString() : ''}`;
        return this.request<Vehicle[]>(path);
    }

    async getVehicleTypes(): Promise<any[] | null> {
        const response = await this.getVehicleAttributes();
        if (!response) return null;

        const vehicles: any[] = [];
        for (const [vehicleId, attributes] of Object.entries(response.attributes)) {
            if (attributes.vehicleType) {
                const latestType = attributes.vehicleType
                    .sort((a, b) => new Date(b.date).getTime() - new Date(a.date).getTime())[0];
                if (latestType) {
                    vehicles.push({
                        vehicleTypeId: latestType.value,
                        vehicleTypeName: latestType.value,
                    });
                }
            }
        }
        return vehicles;
    }

    async getVehicleDepotPeriods(vehicleIds?: string[], date?: string): Promise<Record<string, VehicleDepotPeriod[]> | null> {
        const params = new URLSearchParams();
        if (vehicleIds) {
            vehicleIds.forEach(id => params.append('vehicleIds', id));
        }
        if (date) {
            params.append('date', date);
        }

        const path = `/vehicle/depot-periods${params.toString() ? '?' + params.toString() : ''}`;
        return this.request<Record<string, VehicleDepotPeriod[]>>(path);
    }

    /**
     * Get vehicle attributes
     * @param vehicleIds - optional array of vehicle ids to filter by
     * @returns Record<string, AttributeTypeDefinition[]> - a record of vehicle ids and their attributes
     * 
     * Example response:
     * {
  "attributeMap": {
    "vehicleType": {
      "id": "vehicleType",
      "type": "SINGLE_SELECT",
      "displayName": "Vehicle Type"
    }
  },
  "attributes": {
    "M003": {
      "vehicleType": [
        {
          "attributeId": "vehicleType",
          "date": "1970-01-01",
          "value": "LFL"
        },
        {
          "attributeId": "vehicleType",
          "date": "2025-03-04",
          "value": "CHR"
        }
      ]
    }
     */
    async getVehicleAttributes(vehicleIds?: string[]): Promise<GetVehicleAttributesResponse | null> {
        const params = new URLSearchParams();
        if (vehicleIds) {
            vehicleIds.forEach(id => params.append('vehicleIds', id));
        }

        const path = `/v1/vehicle-attributes${params.toString() ? '?' + params.toString() : ''}`;
        return this.request<GetVehicleAttributesResponse>(path);
    }

    // Operations Management
    async createOperation(operationData: Omit<Operation, 'id' | 'createdAt' | 'updatedAt'>): Promise<Operation | null> {
        return this.request<Operation>('/operations', {
            method: 'POST',
            body: JSON.stringify(operationData)
        });
    }

    async getOperation(operationId: string): Promise<Operation | null> {
        return this.request<Operation>(`/operations/${operationId}`);
    }

    async listOperations(regionId?: string, status?: Operation['status']): Promise<Operation[] | null> {
        const params = new URLSearchParams();
        if (regionId) params.append('regionId', regionId);
        if (status) params.append('status', status);

        const path = `/operations${params.toString() ? '?' + params.toString() : ''}`;
        return this.request<Operation[]>(path);
    }

    async updateOperation(operationId: string, operationData: Partial<Operation>): Promise<Operation | null> {
        return this.request<Operation>(`/operations/${operationId}`, {
            method: 'PATCH',
            body: JSON.stringify(operationData)
        });
    }

        async deleteOperation(operationId: string): Promise<void> {
        await this.request(`/operations/${operationId}`, {
            method: 'DELETE'
        });
    }

    // Operation Schedules
    async createOperationSchedule(scheduleData: Omit<OperationSchedule, 'id'>): Promise<OperationSchedule | null> {
        return this.request<OperationSchedule>('/operation-schedules', {
            method: 'POST',
            body: JSON.stringify(scheduleData)
        });
    }

    async getOperationSchedule(scheduleId: string): Promise<OperationSchedule | null> {
        return this.request<OperationSchedule>(`/operation-schedules/${scheduleId}`);
    }

    async listOperationSchedules(operationId: string, date?: string): Promise<OperationSchedule[] | null> {
        const params = new URLSearchParams();
        params.append('operationId', operationId);
        if (date) params.append('date', date);

        const path = `/operation-schedules${params.toString() ? '?' + params.toString() : ''}`;
        return this.request<OperationSchedule[]>(path);
    }

    async updateOperationSchedule(scheduleId: string, scheduleData: Partial<OperationSchedule>): Promise<OperationSchedule | null> {
        return this.request<OperationSchedule>(`/operation-schedules/${scheduleId}`, {
            method: 'PATCH',
            body: JSON.stringify(scheduleData)
        });
    }

    // Operation Vehicles
    async assignVehicleToOperation(vehicleData: Omit<OperationVehicle, 'id'>): Promise<OperationVehicle | null> {
        return this.request<OperationVehicle>('/operation-vehicles', {
            method: 'POST',
            body: JSON.stringify(vehicleData)
        });
    }

    async getOperationVehicle(vehicleId: string): Promise<OperationVehicle | null> {
        return this.request<OperationVehicle>(`/operation-vehicles/${vehicleId}`);
    }

    async listOperationVehicles(operationId: string): Promise<OperationVehicle[] | null> {
        const params = new URLSearchParams();
        params.append('operationId', operationId);

        const path = `/operation-vehicles${params.toString() ? '?' + params.toString() : ''}`;
        return this.request<OperationVehicle[]>(path);
    }

    async updateOperationVehicle(vehicleId: string, vehicleData: Partial<OperationVehicle>): Promise<OperationVehicle | null> {
        return this.request<OperationVehicle>(`/operation-vehicles/${vehicleId}`, {
            method: 'PATCH',
            body: JSON.stringify(vehicleData)
        });
    }

    // Private Hires
    async createPrivateHireOrder(orderData: PrivateHireOrder): Promise<any | null> {
        return this.request('/private_hires/order', {
            method: 'POST',
            body: JSON.stringify(orderData)
        }, 3, ['orderIdAlreadyExists', 'taskIdsAlreadyExists']);
    }

    async getPrivateHireOrder(orderId: string): Promise<any | null> {
        return this.request(`/private_hires/order/${orderId}/info`, {}, 0, ['orderNonExistent']);
    }

    async privateHireOrderExists(orderId: string): Promise<any | null> {
        const response = await this.getPrivateHireOrder(orderId);
        if (response.errors && response.errors.some((e: any) => e.type === 'orderNonExistent')) {
            return false;
        }
        return true;
    }

    async updatePrivateHireOrderStatus(orderId: string, statusData: PrivateHireOrderStatus): Promise<any | null> {
        return this.request(`/private_hires/order/${orderId}`, {
            method: 'PATCH',
            body: JSON.stringify(statusData)
        }, 3, ['orderIdAlreadyExists', 'taskIdsAlreadyExists', 'privateHireTripsWithoutChanges', 'orderAlreadyCancelled']);
    }

    async updatePrivateHireOrderTrips(orderId: string, tripsData: { privateHires: (PrivateHireTask | { taskId: string; status: 'cancelled' })[] }): Promise<any | null> {
        return this.request(`/private_hires/order/${orderId}/trips`, {
            method: 'PATCH',
            body: JSON.stringify(tripsData)
        }, 3, ['orderIdAlreadyExists', 'taskIdsAlreadyExists', 'privateHireTripsWithoutChanges', 'orderAlreadyCancelled']);
    }

    // Real-time Operations
    async getRealTimeOperations(regionId: string, fromDate: string, toDate: string): Promise<RealTimeOperation[] | null> {
        const params = new URLSearchParams();
        params.append('region_id', regionId);
        params.append('from_date', fromDate);
        params.append('to_date', toDate);

        const path = `/real-time-operations${params.toString() ? '?' + params.toString() : ''}`;
        return this.request<RealTimeOperation[]>(path);
    }

    // Operational Plan V2
    async getOperationalPlanV2(params: {
        depotUuids: string[];
        driverUuids?: string[];
        fromDate: string;
        toDate: string;
        includeUnassigned?: boolean;
    }): Promise<OperationalPlanDepot[] | null> {
        const queryParams = new URLSearchParams();
        params.depotUuids.forEach(uuid => queryParams.append('depotUuids', uuid));
        if (params.driverUuids) {
            params.driverUuids.forEach(uuid => queryParams.append('driverUuids', uuid));
        }
        queryParams.append('fromDate', params.fromDate);
        queryParams.append('toDate', params.toDate);
        if (params.includeUnassigned) {
            queryParams.append('includeUnassigned', 'true');
        }

        const path = `/v2/operational-plan${queryParams.toString() ? '?' + queryParams.toString() : ''}`;
        return this.request<OperationalPlanDepot[]>(path);
    }

    // Calendar
    async getCalendarInfo(params: {
        depotId: string;
        from: string;
        to: string;
        plan?: string[];
        group?: string[];
        type?: string[];
        assignedOnly?: boolean;
        includeStops?: boolean;
        driversIds?: string[];
    }): Promise<CalendarInformation | null> {
        const queryParams = new URLSearchParams();
        queryParams.append('from', params.from);
        queryParams.append('to', params.to);
        
        if (params.plan) {
            params.plan.forEach(p => queryParams.append('plan', p));
        }
        if (params.group) {
            params.group.forEach(g => queryParams.append('group', g));
        }
        if (params.type) {
            params.type.forEach(t => queryParams.append('type', t));
        }
        if (params.assignedOnly !== undefined) {
            queryParams.append('assignedOnly', params.assignedOnly.toString());
        }
        if (params.includeStops !== undefined) {
            queryParams.append('includeStops', params.includeStops.toString());
        }
        if (params.driversIds) {
            params.driversIds.forEach(id => queryParams.append('driversIds', id));
        }

        const path = `/${params.depotId}/calendar${queryParams.toString() ? '?' + queryParams.toString() : ''}`;
        return this.request<CalendarInformation>(path);
    }
} 